import { createApp, h } from 'vue'
import { createInertiaApp } from '@inertiajs/vue3'
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import * as Sentry from "@sentry/vue";
import gsap from 'gsap';
import { CSSPlugin } from 'gsap/CSSPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins globally
gsap.registerPlugin(CSSPlugin, ScrollTrigger);

import {
  faLinkedinIn,
} from "@fortawesome/free-brands-svg-icons";

import {
  faPlus as farPlus,
  faGlobe as farGlobe,
} from "@fortawesome/pro-regular-svg-icons";

import {
  faBars,
  faChevronDown,
  faChevronRight,
  faXmark,
  faInfoCircle,
  faChevronLeft,
  faHouseBuilding,
  faCar,
  faCarBuilding,
  faGarageCar,
  faSpeaker,
  faBooks,
  faRacquet,
  faChartBar,
  faUsers,
  faMailBulk,
  faPaintBrush,
  faPaperPlane,
  faTrashCan,
  faFileAlt,
  faEnvelopeOpenText,
  faGlobe,
  faFolder,
  faHeadset,
  faInbox,
  faPlus,
  faCircleNotch,
  faCheckCircle,
  faExclamationCircle,
  faBus,
  faTrain,
  faPhone,
  faEnvelope,
  faMapLocationDot,
  faLocationDot,        // Added this icon
  faMessages,
  faLink,
  faMobile,
  faTools,
  faHandPointer,
  faShieldAlt,
  faSync,
  faMobileAlt,
  faChartLine,
  faPlug,
  faRobot,
  faWindow,
  faNewspaper,
  faCartShopping,
  faCreditCard,
  faCalendarDays,
  faBookmark,
  faTrophy,
  faUser,
  faBuilding,
  faMessage,
  faTag,
  faArrowRight,         // Added this icon
  faCheck,
  faRocket
} from "@fortawesome/pro-solid-svg-icons";

import PrimeVue from "primevue/config";
import Aura from '@primevue/themes/aura';
import Layout from './Pages/Layout/Layout.vue';
import { MotionPlugin } from '@vueuse/motion'

createInertiaApp({
  resolve: name => {
    const pages = import.meta.glob(['./Pages/*.vue', './Pages/**/*.vue', './Pages/**/**/*.vue'], { eager: true })
    let page = pages[`./Pages/${name}.vue`]
    page.default.layout = Layout;
    return page;
  },
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) }).use(plugin);

    library.add(
        // Brands
        faLinkedinIn,
        
        // Pro Regular
        farPlus, farGlobe,
        
        // Pro Solid
        faBars, faChevronDown, faChevronRight, faXmark, faInfoCircle, faChevronLeft, faHouseBuilding, faCar, faCarBuilding, faGarageCar, faSpeaker, faBooks, 
        faRacquet, faChartBar, faUsers, faMailBulk, faPaintBrush, faPaperPlane, faTrashCan, faFileAlt, faEnvelopeOpenText, faGlobe, faFolder, faHeadset, 
        faInbox, faPlus, faMessages, faCircleNotch, faCheckCircle, faExclamationCircle, faMapLocationDot, faLocationDot, faBus, faTrain, faPhone, faEnvelope,
        faLink, faMobile, faTools, faHandPointer, faShieldAlt, faSync, faMobileAlt, faChartLine, faPlug, faRobot, faWindow, faNewspaper, faCartShopping, 
        faCreditCard, faCalendarDays, faBookmark, faTrophy, faUser, faBuilding, faMessage, faTag, faArrowRight, faCheck, faRocket
    );

    Sentry.init({
      app,
      dsn: "https://b764386e94389500d8be28c820df54ba@o4508357877039104.ingest.de.sentry.io/4509246595661904",
      integrations: [],
    });

    app.component('font-awesome-icon', FontAwesomeIcon);
    
    app.use(MotionPlugin)
    app.use(PrimeVue, {
        theme: {
            preset: Aura
        }
    });
    
    app.mount(el)
  },
}).then(() => {
  // Add global dark mode toggle after Inertia app is mounted
  initializeDarkModeToggle();
});

function initializeDarkModeToggle() {
  const toggleContainer = document.getElementById('dark-mode-toggle');
  if (!toggleContainer) {
    console.error('Dark mode toggle container not found');
    return;
  }

  // Check initial state
  const savedMode = localStorage.getItem('darkMode');
  let isDarkMode = false;
  
  if (savedMode === 'true') {
    isDarkMode = true;
  } else if (savedMode === 'false') {
    isDarkMode = false;
  } else {
    isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  // Apply initial state more forcefully
  const htmlElement = document.documentElement;
  
  function applyDarkMode(enable) {
    if (enable) {
      htmlElement.classList.remove('light');
      htmlElement.classList.add('dark');
      htmlElement.setAttribute('data-theme', 'dark');
    } else {
      htmlElement.classList.remove('dark');
      htmlElement.classList.add('light');
      htmlElement.setAttribute('data-theme', 'light');
    }
    
    // Force repaint by accessing offsetHeight
    document.body.offsetHeight;
    
    // Dispatch a custom event for components to listen to
    window.dispatchEvent(new CustomEvent('darkModeToggle', { detail: { isDark: enable } }));
  }
  
  // Apply initial state
  applyDarkMode(isDarkMode);

  console.log('🌙 Dark mode initialized:', isDarkMode);
  console.log('📝 HTML classes:', htmlElement.className);

  // Create toggle button
  const toggleButton = document.createElement('button');
  toggleButton.className = 'p-3 rounded-full bg-white/90 dark:bg-zinc-800/90 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700 shadow-lg hover:shadow-xl transition-all duration-300 group';
  
  // Function to update button content
  function updateButton() {
    if (isDarkMode) {
      toggleButton.innerHTML = `
        <svg class="w-5 h-5 text-yellow-500 transition-transform duration-300 group-hover:rotate-12" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
        </svg>
      `;
      toggleButton.title = 'Switch to Light Mode';
    } else {
      toggleButton.innerHTML = `
        <svg class="w-5 h-5 text-zinc-700 transition-transform duration-300 group-hover:-rotate-12" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      `;
      toggleButton.title = 'Switch to Dark Mode';
    }
  }
  
  // Function to toggle dark mode
  function toggleDarkMode() {
    isDarkMode = !isDarkMode;
    
    console.log('🔄 Toggling dark mode to:', isDarkMode);
    
    // Apply the new state
    applyDarkMode(isDarkMode);
    
    // Save to localStorage
    localStorage.setItem('darkMode', isDarkMode.toString());
    
    updateButton();
    
    console.log('📝 HTML classes after toggle:', htmlElement.className);
  }
  
  // Set up button
  updateButton();
  toggleButton.addEventListener('click', toggleDarkMode);
  
  // Add button to container
  toggleContainer.appendChild(toggleButton);
  
  console.log('✅ Dark mode toggle initialized');
}